import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import 'antd/dist/antd.css'

import { apiManager, constants, isSupportedBrowser } from 'helpers'
import { useAuthState, useQuery } from 'hooks'

import BrowserCompatibilityModal from 'components/BrowserCompatibilityModal'
// import PreLobbyEventModal from 'components/PreLobbyEventModal'
import DashboardSection from './components/DashboardSection'
import EventsFilter from './components/EventsFilter'
import WelcomeModal from './components/WelcomeModal'
import NewTicketModal from './components/NewTicketModal'

const {
  ROUTES: { LANDING, EVENT },
  EVENT_CONSTANTS: {
    TICKETS: { NEW_EVENT_PARAMS_KEY, WAIT_LIST_PARAMS_KEY, JOIN_EVENT_PARAMS_KEY },
  },
} = constants

const UPCOMING_EVENTS_FILTER = 'upcoming'
const PAST_EVENTS_FILTER = 'past'

const FILTER_URL_PARAMS_KEY = 'type'

const filters = [
  {
    key: UPCOMING_EVENTS_FILTER,
    label: 'Upcoming',
    fetcher: apiManager.getDashboardEvents,
    emptyState: {
      text: 'You have no upcoming events',
      cta: 'See past events',
      ctaFilter: PAST_EVENTS_FILTER,
    },
  },
  {
    key: PAST_EVENTS_FILTER,
    label: 'Past',
    fetcher: apiManager.getDashboardPastEvents,
    emptyState: {
      text: 'You have no past events',
      cta: 'See upcoming events',
      ctaFilter: UPCOMING_EVENTS_FILTER,
    },
  },
]

const newEventView = true

const Dashboard = ({ location: { search }, history }) => {
  const { user } = useAuthState()

  const { initialFilter, searchParams } = useMemo(() => {
    const searchParams = new URLSearchParams(search)
    const initialFilter =
      filters.find(({ key }) => key === searchParams.get(FILTER_URL_PARAMS_KEY))?.key ||
      UPCOMING_EVENTS_FILTER

    return {
      searchParams,
      initialFilter,
    }
  }, [search])

  const [showWelcomeModal, setShowWelcomeModal] = useState(false)
  const [newEvent, setNewEvent] = useState()
  const [preSetupEventId, setPreSetupEventId] = useState()
  const [currentFilter, setCurrentFilter] = useState(initialFilter)
  const [lastTimeFiltered, setLastTimeFiltered] = useState(Date.now())
  const { fetcher, emptyState } = useMemo(
    () => filters.find(({ key }) => key === currentFilter),
    [currentFilter]
  )

  const { isLoading, data: events } = useQuery(currentFilter, fetcher)

  const openWelcomeModal = useCallback(() => {
    if (searchParams.get(WAIT_LIST_PARAMS_KEY)) {
      setTimeout(() => setShowWelcomeModal(true), 1000)
    }
  }, [searchParams, setShowWelcomeModal])

  useEffect(
    () => {
      if (!events?.length) {
        return
      }

      const getEventById = eventId => events.find(({ id }) => id === parseInt(eventId))

      const joinEventId = searchParams.get(JOIN_EVENT_PARAMS_KEY)
      if (joinEventId) {
        const event = getEventById(joinEventId)
        event && setPreSetupEventId(event.id)
        return
      }

      if (newEventView) {
        return
      }

      const newEventId = searchParams.get(NEW_EVENT_PARAMS_KEY)
      if (newEventId) {
        setNewEvent(getEventById(newEventId))
      } else {
        openWelcomeModal()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events]
  )

  const handleFilter = filter => {
    setCurrentFilter(filter)
    setLastTimeFiltered(Date.now())
    searchParams.set(FILTER_URL_PARAMS_KEY, filter)
    history.replace(`?${searchParams.toString()}`)
  }

  const removeUrlParam = useCallback(
    paramKey => {
      searchParams.delete(paramKey)
      history.replace(`?${searchParams.toString()}`)
    },
    [history, searchParams]
  )

  const handleCloseModal = useCallback(() => {
    setPreSetupEventId(null)
    removeUrlParam(JOIN_EVENT_PARAMS_KEY)
  }, [removeUrlParam])

  /*
  const handleClosePreLobbyEventModal = useCallback(() => {
    handleCloseModal()
    openWelcomeModal()
  }, [handleCloseModal, openWelcomeModal])
   */

  const handleNewTicketModalClose = useCallback(
    eventSetupId => {
      setNewEvent(null)
      removeUrlParam(NEW_EVENT_PARAMS_KEY)

      if (!eventSetupId) {
        openWelcomeModal()
      }
    },
    [removeUrlParam, openWelcomeModal]
  )

  const handleWelcomeModalClose = useCallback(() => {
    setShowWelcomeModal(false)
    removeUrlParam(WAIT_LIST_PARAMS_KEY)
  }, [removeUrlParam])

  const handleEnterEvent = useCallback(
    id => {
      if (newEventView && id) {
        history.push(generatePath(EVENT, { event_id: id }))
        return
      }

      setPreSetupEventId(id)
    },
    [history]
  )

  return (
    <div className="relative pt-5 md:pt-0">
      {!newEventView && (
        <>
          {isSupportedBrowser ? (
            // <PreLobbyEventModal eventId={preSetupEventId} onClose={handleClosePreLobbyEventModal} />
            <React.Fragment />
          ) : (
            <BrowserCompatibilityModal show={!!preSetupEventId} onClose={handleCloseModal} />
          )}
        </>
      )}
      <EventsFilter filters={filters} currentFilter={currentFilter} onFilter={handleFilter} />
      <DashboardSection
        key={lastTimeFiltered}
        user={user}
        events={events}
        isLoading={isLoading}
        emptyState={emptyState}
        onFilter={handleFilter}
        onEnterEvent={handleEnterEvent}
        onDiscoverEventsClick={() => history.push(LANDING)}
      />
      <WelcomeModal
        show={showWelcomeModal}
        onClose={handleWelcomeModalClose}
        onDiscoverClick={() => history.push(LANDING)}
      />
      <NewTicketModal
        event={newEvent}
        show={!!newEvent}
        onClose={() => handleNewTicketModalClose()}
        onEnterEvent={() => {
          setPreSetupEventId(newEvent.id)
          handleNewTicketModalClose(newEvent.id)
        }}
      />
    </div>
  )
}

export default Dashboard
